import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { isEmpty, sortByKey } from '@semios/app-platform-common'
import { useEffect, useState } from 'react'
import { useApiREST } from 'utils/useApiREST'
import type { Alert } from '../_types'
import { shapeLegacyAlertV3Alerts } from './shapeLegacyAlertV3Alerts'
import { shapeWeatherForecastAlerts } from './shapeWeatherForecastAlerts'

const allAlertsRequest = {
  legacyV3Alerts: {
    attributes: {
      alertId: true,
      alertType: true,
      blockIds: true,
      createdAt: true,
      deletedAt: true,
      endDate: true,
      frequencyMinutes: true,
      insectId: true,
      name: true,
      notifyOwnerByEmail: true,
      notifyOwnerBySMS: true,
      propertyIds: true,
      sleepMinutes: true,
      snoozeAllUntil: true,
      snoozeUntil: true,
      startDate: true,
      stationGeoms: true,
      updatedAt: true,
      contacts: {
        contactId: true,
        contactViaEmail: true,
        contactViaSMS: true,
        email: true,
        language: true,
        name: true,
        sms: true,
      },
      groups: {
        contactViaEmail: true,
        contactViaSMS: true,
        groupId: true,
        name: true,
        contactsForGroup: {
          contactId: true,
          email: true,
          language: true,
          name: true,
          sms: true,
        },
      },
      alertRules: {
        key: true,
        ruleId: true,
        operator: true,
        unit: true,
        value: true,
      },
    },
  },
  legacyWeatherForecastAlerts: {
    attributes: {
      alertId: true,
      blockIds: true,
      createdAt: true,
      deletedAt: true,
      endDate: true,
      name: true,
      notifyOwnerByEmail: true,
      notifyOwnerBySMS: true,
      propertyIds: true,
      hourOfDay: true,
      startDate: true,
      stationGeoms: true,
      updatedAt: true,
      contacts: {
        contactId: true,
        contactViaEmail: true,
        contactViaSMS: true,
        email: true,
        language: true,
        name: true,
        sms: true,
      },
      groups: {
        contactViaEmail: true,
        contactViaSMS: true,
        groupId: true,
        name: true,
        contactsForGroup: {
          contactId: true,
          email: true,
          language: true,
          name: true,
          sms: true,
        },
      },
      alertRules: true,
    },
  },
} as const

export type AlertsResponse = routes.UserProfile.Response<typeof allAlertsRequest>['data']

export const useAlerts = () => {
  const [error, setError] = useState<unknown>(null)
  const [alerts, setAlerts] = useState<Alert[]>([])

  const {
    loading,
    refreshHandle,
    error: fetchError,
  } = useApiREST({
    body: allAlertsRequest,
    url: '/user-profile',
    initialLoading: true,
    shaper: (response) => {
      if (!isEmpty(response.errors)) {
        setError(response.errors)

        setAlerts(() => [])
      }

      const v3AlertsShaped = shapeLegacyAlertV3Alerts(response.data as AlertsResponse)
      const weatherForecastAlertsShaped = shapeWeatherForecastAlerts(response.data as AlertsResponse)
      const result = [...v3AlertsShaped, ...weatherForecastAlertsShaped].sort(sortByKey('createdAt', -1))

      setAlerts(() => result)
    },
  })

  useEffect(() => {
    if (fetchError) {
      setError(fetchError)
    }
  }, [fetchError])

  const deleteAlertHandler = (deletedAlertId: Alert['id']) => {
    setAlerts((prevAlerts) => prevAlerts.filter((alert) => alert.id !== deletedAlertId))
  }

  return {
    alerts,
    loading,
    refreshHandle,
    error,
    deleteAlertHandler,
  }
}
